import { motion } from "framer-motion";
import { Instagram, Linkedin, Phone, Mail, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SiteSettings {
  instagram_url: string | null;
  linkedin_url: string | null;
  whatsapp_url: string | null;
  email: string | null;
}

export const Footer = () => {
  const [settings, setSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("instagram_url, linkedin_url, whatsapp_url, email")
        .single();
      if (data) setSettings(data);
    };
    fetchSettings();
  }, []);

  // Extract phone from whatsapp URL (format: https://wa.me/5511975489900)
  const formatPhone = (url: string | null) => {
    if (!url) return null;
    const match = url.match(/wa\.me\/(\d+)/);
    if (match) {
      const num = match[1];
      // Format: +55 11 97548-9900
      if (num.length === 13) {
        return `+${num.slice(0, 2)} ${num.slice(2, 4)} ${num.slice(4, 9)}-${num.slice(9)}`;
      }
      return num;
    }
    return null;
  };

  const phone = formatPhone(settings?.whatsapp_url);
  const email = settings?.email || "sotsekadvocacia@gmail.com";

  return (
    <footer className="py-12 border-t border-border">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          {/* Logo */}
          <motion.a
            href="#"
            className="flex items-center gap-3 group"
            whileHover={{ scale: 1.02 }}
          >
            <span className="font-serif text-2xl font-medium tracking-tight text-foreground">
              Sotsek
            </span>
          </motion.a>

          {/* Contact Info */}
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-sm text-muted-foreground">
            {phone && (
              <a
                href={settings?.whatsapp_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-foreground transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span>{phone}</span>
              </a>
            )}
            <a
              href={`mailto:${email}`}
              className="flex items-center gap-2 hover:text-foreground transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span>{email}</span>
            </a>
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>R. Francisco Leitão, 653 - Pinheiros, SP</span>
            </span>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            <motion.a
              href={settings?.instagram_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Seguir no Instagram"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Instagram className="w-4 h-4" aria-hidden="true" />
            </motion.a>
            <motion.a
              href={settings?.linkedin_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Seguir no LinkedIn"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Linkedin className="w-4 h-4" aria-hidden="true" />
            </motion.a>
          </div>
        </div>
      </div>
    </footer>
  );
};
